import type { RegisteredTenantCreateOperation } from '@festina/configuration-general-types/configuration'
import { Yup } from '@festina-life/components'
import { typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { toast } from '@festina-life/ui/feedback'
import { FormikForm, FormikTextInput } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'

import type { OperationWithSourceInfo } from '../../uiModels/types'
import { useOperationsContext } from '../../workspace/operationsContext'

type UpdateTenantFormInput = {
  name: string
}

const validate = typeUtils.createValidator<UpdateTenantFormInput>()

type Props = {
  operation: OperationWithSourceInfo<RegisteredTenantCreateOperation>
}

export const UpdateRegisteredTenantForm = ({ operation }: Props) => {
  const translate = useTranslate()
  const { onAddOrUpdateOperation } = useOperationsContext()

  const handleUpdateTenant = async (values: UpdateTenantFormInput) => {
    const nextOperation = {
      ...operation.internalOperation,
      operationKey: operation.isDraft
        ? operation.internalOperation.operationKey
        : undefined,
      registeredTenant: {
        _type: 'RegisteredTenant',
        ...operation.internalOperation.registeredTenant,
        tenantName: values.name,
      },
    } satisfies RegisteredTenantCreateOperation

    try {
      await onAddOrUpdateOperation({
        operation: nextOperation,
        block: operation.block,
        layer: operation.layer,
        shouldOpenUnifiedBranchWorkspace: true,
      })
      toast({
        title: translate('tenantViewer.form.name.success'),
        variant: 'success',
      })
    } catch {
      toast({
        title: translate('tenantViewer.form.name.error'),
        variant: 'error',
      })
    }
  }

  return (
    <FormikForm
      initialValues={{
        name: operation.internalOperation.registeredTenant?.tenantName ?? '',
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required(translate('validation.required')),
      })}
      onSubmit={handleUpdateTenant}
      className="w-sm"
      enableReinitialize
    >
      {(formValues) => (
        <>
          <FormikTextInput
            name={validate('name')}
            label={translate('tenantViewer.form.name.label')}
          />
          <Button type="submit" isLoading={formValues.isSubmitting}>
            {translate('tenantViewer.form.submit.label')}
          </Button>
        </>
      )}
    </FormikForm>
  )
}
